import React, { createContext, useCallback, useContext, useState } from 'react'

import { MessageCard } from '@/components/custom-components'
import { MessageOptions, MessageResponse } from '@/types/message'

// Extended options type to include callback handlers
interface CardMessageOptions extends MessageOptions {
    onYes?: () => void
    onNo?: () => void
    onCancel?: () => void
}

interface CardMessageContextType {
    showCardMessage: (
        options: Omit<CardMessageOptions, 'onYes' | 'onNo' | 'onCancel'>
    ) => Promise<MessageResponse>
}

const CardMessageContext = createContext<CardMessageContextType | undefined>(
    undefined
)

export function CardMessageProvider({
    children
}: {
    children: React.ReactNode
}): JSX.Element {
    const [isVisible, setIsVisible] = useState(false)
    const [messageConfig, setMessageConfig] =
        useState<CardMessageOptions | null>(null)

    const showCardMessage = useCallback(
        (
            options: Omit<CardMessageOptions, 'onYes' | 'onNo' | 'onCancel'>
        ): Promise<MessageResponse> => {
            return new Promise((resolve) => {
                const config: CardMessageOptions = {
                    ...options,
                    onYes: () => {
                        setIsVisible(false)
                        setMessageConfig(null)
                        resolve(MessageResponse.ACCEPTED)
                    },
                    onNo: () => {
                        setIsVisible(false)
                        setMessageConfig(null)
                        resolve(MessageResponse.REJECTED)
                    },
                    onCancel: () => {
                        setIsVisible(false)
                        setMessageConfig(null)
                        resolve(MessageResponse.CANCELED)
                    }
                }

                setMessageConfig(config)
                setIsVisible(true)
            })
        },
        []
    ) // Empty dependency array since we don't use any external values

    return (
        <CardMessageContext.Provider value={{ showCardMessage }}>
            {children}
            {messageConfig && (
                <MessageCard
                    {...messageConfig}
                    isVisible={isVisible}
                />
            )}
        </CardMessageContext.Provider>
    )
}

export const useCardMessage = (): CardMessageContextType => {
    const context = useContext(CardMessageContext)

    if (!context) {
        throw new Error(
            'useCardMessage must be used within a CardMessageProvider'
        )
    }

    return context
}
